<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
  requiresAdmin: true
</route>

<script setup>
// Admin → Activity (RM-451, Kapil and the product agent 11/3). Intent: what
// are Platinum members, Free Tier members and restaurant accounts doing, and
// is there an RM AI conversation to step into — in one scan, on a phone too.
// Everything shown comes from GET /activitylog/view (lib/activity/view.js),
// the view the 6 AM and 4 PM digest emails render, so the page and the email
// always agree. RM Team AI & Admin, Anonymous and Crawlers are app-only;
// Anonymous and Crawlers start switched off.
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from '@axios'
import {
  RANGES, rangePeriod, periodLabel, relativeTime, defaultChips, matchPeople, pagesLine, exportParams,
} from '@/utils/activityFeed'

const route = useRoute()
const router = useRouter()

const view = ref(null)
const loading = ref(false)
const exporting = ref(false)
const error = ref('')
const rangeKey = ref('today')
const fromDate = ref('')
const toDate = ref('')
const search = ref('')
const chips = ref([])
const expandedQuotes = ref(new Set())

// A digest email links here with its exact window, so the page
// opens on the same period and the same counts as the email.
// (?since&until — named so they can't be mistaken for a redirect target).
const linkPeriod = ref(route.query.since && route.query.until ? { from: String(route.query.since), to: String(route.query.until) } : null)

// "Today so far" and "Last 7 days" end now: picking the range again (or
// coming back to the tab) moves the end to the current time.
const nowTick = ref(Date.now())

const period = computed(() => {
  if (linkPeriod.value) return linkPeriod.value

  return rangePeriod(rangeKey.value, { now: new Date(nowTick.value), fromDate: fromDate.value, toDate: toDate.value })
})

const categories = computed(() => (view.value ? view.value.categories : []))
const memberChipKeys = computed(() => categories.value.filter(c => c.key !== 'anonymous' && c.key !== 'crawler').map(c => c.key))
const offChipKeys = computed(() => categories.value.filter(c => c.key === 'anonymous' || c.key === 'crawler').map(c => c.key))

const sections = computed(() => {
  if (!view.value) return []

  return categories.value
    .filter(c => chips.value.includes(c.key))
    .map(c => ({ ...c, people: matchPeople(view.value.groups[c.key] || [], search.value) }))
    .filter(s => s.people.length)
})

const hiddenOff = computed(() => {
  if (!view.value) return []
  const off = []
  if (!chips.value.includes('anonymous') && view.value.offCounts.anonymousPageViews) off.push({ key: 'anonymous', text: `${view.value.offCounts.anonymousPageViews} anonymous page views`, action: 'Show anonymous' })
  if (!chips.value.includes('crawler') && view.value.offCounts.crawlerPageViews) off.push({ key: 'crawler', text: `${view.value.offCounts.crawlerPageViews} crawler page views`, action: 'Show crawlers' })

  return off
})

async function load() {
  const p = period.value
  if (!p) return
  loading.value = true
  error.value = ''
  try {
    const res = await axios.get('/activitylog/view', { params: { from: p.from, to: p.to } })
    const firstLoad = !view.value
    view.value = res.data
    if (firstLoad) chips.value = defaultChips(res.data.categories)
  } catch (e) {
    error.value = (e.response && e.response.data && e.response.data.error) || 'The activity could not be loaded. Try again.'
  } finally {
    loading.value = false
  }
}

function pickRange(key) {
  linkPeriod.value = null
  if (route.query.since || route.query.until) router.replace({ query: {} })
  rangeKey.value = key
  nowTick.value = Date.now()
  if (key === 'custom' && !fromDate.value) {
    const today = new Date().toISOString().slice(0, 10)
    fromDate.value = today
    toDate.value = today
  }
}

function toggleChip(key) {
  chips.value = chips.value.includes(key) ? chips.value.filter(k => k !== key) : [...chips.value, key]
}

function toggleQuotes(rowKey) {
  const next = new Set(expandedQuotes.value)
  if (next.has(rowKey)) next.delete(rowKey)
  else next.add(rowKey)
  expandedQuotes.value = next
}

async function downloadExport() {
  const p = period.value
  if (!p) return
  exporting.value = true
  try {
    const res = await axios.get('/activitylog/export', { params: exportParams(p, chips.value), responseType: 'blob' })
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    const day = s => s.slice(0, 10)
    a.href = url
    a.download = `rm-activity-${day(p.from)}-to-${day(p.to)}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } catch (e) {
    error.value = 'The export could not be downloaded. Try again.'
  } finally {
    exporting.value = false
  }
}

watch(period, (p, old) => {
  if (p && (!old || p.from !== old.from || p.to !== old.to)) load()
})
function refreshOnReturn() {
  if (document.visibilityState === 'visible' && !linkPeriod.value && rangeKey.value !== 'yesterday') nowTick.value = Date.now()
}
onMounted(() => {
  load()
  document.addEventListener('visibilitychange', refreshOnReturn)
})
onUnmounted(() => document.removeEventListener('visibilitychange', refreshOnReturn))
</script>

<template>
  <div class="af-page content-wrapper">
    <header class="af-head">
      <h1 class="af-title">
        Activity
      </h1>
      <div
        class="af-range"
        role="tablist"
        aria-label="Period"
      >
        <button
          v-for="r in RANGES"
          :key="r.key"
          type="button"
          role="tab"
          class="af-range-btn"
          :class="{ on: !linkPeriod && rangeKey === r.key }"
          :aria-selected="!linkPeriod && rangeKey === r.key"
          @click="pickRange(r.key)"
        >
          {{ r.label }}
        </button>
      </div>
    </header>

    <div
      v-if="linkPeriod"
      class="af-period"
    >
      {{ periodLabel(linkPeriod.from, linkPeriod.to) }}, as in the digest email
    </div>

    <div
      v-if="!linkPeriod && rangeKey === 'custom'"
      class="af-dates"
    >
      <label class="af-date">
        <span>From</span>
        <input
          v-model="fromDate"
          type="date"
        >
      </label>
      <label class="af-date">
        <span>To</span>
        <input
          v-model="toDate"
          type="date"
        >
      </label>
    </div>

    <p
      v-if="error"
      class="af-state af-error"
    >
      {{ error }}
    </p>

    <div
      v-if="loading && !view"
      class="af-skeleton"
    >
      <div
        v-for="n in 4"
        :key="n"
        class="af-skeleton-row"
      />
    </div>

    <template v-if="view">
      <div
        class="af-summary"
        :class="{ 'af-dim': loading }"
      >
        <div
          v-for="s in view.summary"
          :key="s.key"
          class="af-stat"
        >
          <b>{{ s.count }}</b>
          <span>{{ s.label }}</span>
        </div>
      </div>
      <p
        v-if="view.followUps && view.followUps.length && view.followUps.some(f => f.count)"
        class="af-followups"
      >
        <span
          v-for="f in view.followUps"
          :key="f.label"
        >{{ f.label }}: <b>{{ f.count }}</b></span>
      </p>

      <div class="af-tools">
        <div
          class="af-chips"
          role="group"
          aria-label="Show"
        >
          <button
            v-for="c in categories.filter(c => memberChipKeys.includes(c.key))"
            :key="c.key"
            type="button"
            class="af-chip"
            :class="{ on: chips.includes(c.key) }"
            :aria-pressed="chips.includes(c.key)"
            @click="toggleChip(c.key)"
          >
            {{ c.label }} <span class="n">{{ c.count }}</span>
          </button>
          <span
            class="af-sep"
            aria-hidden="true"
          />
          <button
            v-for="c in categories.filter(c => offChipKeys.includes(c.key))"
            :key="c.key"
            type="button"
            class="af-chip"
            :class="{ on: chips.includes(c.key) }"
            :aria-pressed="chips.includes(c.key)"
            @click="toggleChip(c.key)"
          >
            {{ c.label }} <span class="n">{{ c.count }}</span>
          </button>
        </div>
        <div class="af-tools-right">
          <input
            v-model="search"
            class="af-search"
            type="search"
            placeholder="Search name or email"
            aria-label="Search name, email or restaurant"
          >
          <button
            type="button"
            class="af-export"
            :disabled="exporting || !chips.length"
            @click="downloadExport"
          >
            {{ exporting ? 'Preparing…' : 'Download CSV' }}
          </button>
        </div>
      </div>

      <p
        v-if="!sections.length"
        class="af-state"
      >
        {{ search ? 'No one in the groups shown matches that search.' : 'No activity in the groups shown for this period.' }}
      </p>

      <section
        v-for="s in sections"
        :key="s.key"
        class="af-group"
        :class="{ 'af-dim': loading }"
      >
        <div class="af-group-h">
          <h2>{{ s.label }}</h2>
          <span v-if="s.key === 'anonymous' || s.key === 'crawler'">{{ s.people.length }} {{ s.key === 'crawler' ? (s.people.length === 1 ? 'crawler' : 'crawlers') : (s.people.length === 1 ? 'visitor' : 'visitors') }}</span>
          <span v-else>{{ s.people.length }} active</span>
        </div>

        <article
          v-for="p in s.people"
          :key="p.key"
          class="af-person"
          :class="{ compact: !p.items.length, attention: p.attention }"
        >
          <div class="af-person-h">
            <span class="af-name">{{ p.name }}</span>
            <span
              v-if="p.email"
              class="af-meta"
            >{{ p.email }}</span>
            <span
              v-if="p.meta"
              class="af-meta"
            >{{ p.meta }}</span>
            <span
              v-if="p.laterMember"
              class="af-meta af-signal"
            >later signed in as {{ p.laterMember }}</span>
            <span
              class="af-last"
              :title="relativeTime(p.lastAt)"
            >{{ p.lastClock }}</span>
          </div>

          <div
            v-for="(i, idx) in p.items"
            :key="p.key + idx"
            class="af-row"
            :class="'k-' + i.kind"
          >
            <div
              class="af-t"
              :title="relativeTime(i.at)"
            >
              {{ i.clock }}
            </div>
            <div class="af-what">
              <template v-if="i.type === 'ai'">
                <b>{{ i.label }}</b>
                <span class="af-d"> {{ i.messages.length === 1 ? '1 message' : i.messages.length + ' messages' }}</span>
                <span
                  v-if="i.tag"
                  class="af-tag"
                >{{ i.tag }}</span>
                <span
                  v-if="i.onBehalf"
                  class="af-tag af-tag-plain"
                >{{ i.onBehalf }}</span>
                <div class="af-quotes">
                  <div
                    v-for="(m, mi) in (expandedQuotes.has(p.key + idx) ? i.messages : i.messages.slice(0, 3))"
                    :key="mi"
                  >
                    <span
                      class="af-qt"
                      :title="relativeTime(m.at)"
                    >{{ m.clock }}</span>“{{ m.text }}”
                  </div>
                  <button
                    v-if="i.messages.length > 3"
                    type="button"
                    class="af-more"
                    @click="toggleQuotes(p.key + idx)"
                  >
                    {{ expandedQuotes.has(p.key + idx) ? 'Show fewer' : `Show ${i.messages.length - 3} more` }}
                  </button>
                </div>
              </template>
              <template v-else>
                <span
                  v-if="i.kind === 'rm'"
                  class="af-by"
                >RM</span>
                <b>{{ i.label }}</b>
                <span
                  v-if="i.detail"
                  class="af-d"
                > {{ i.detail }}</span>
                <span
                  v-if="i.kind === 'attention'"
                  class="af-tag af-tag-strong"
                >Needs RM review</span>
                <span
                  v-else-if="i.tag"
                  class="af-tag"
                >{{ i.tag }}</span>
              </template>
            </div>
          </div>

          <div
            v-if="p.pages"
            class="af-row af-pv"
          >
            <div
              class="af-t"
              :title="relativeTime(p.pages.at)"
            >
              {{ p.items.length ? '' : p.pages.clock }}
            </div>
            <div class="af-what">
              {{ pagesLine(p.pages) }}
            </div>
          </div>
        </article>
      </section>

      <div
        v-if="hiddenOff.length && !search"
        class="af-off"
      >
        <span>Not shown:</span>
        <span
          v-for="o in hiddenOff"
          :key="o.key"
        >{{ o.text }}</span>
        <button
          v-for="o in hiddenOff"
          :key="o.key + 'btn'"
          type="button"
          class="af-link"
          @click="toggleChip(o.key)"
        >
          {{ o.action }}
        </button>
      </div>
    </template>
  </div>
</template>

<style scoped>
.af-page {
  --af-ink: rgb(var(--v-theme-on-surface));
  --af-body: rgba(var(--v-theme-on-surface), 0.78);
  --af-muted: rgba(var(--v-theme-on-surface), 0.55);
  --af-line: rgba(var(--v-theme-on-surface), 0.1);
  --af-inset: rgba(var(--v-theme-on-surface), 0.05);
  --af-card: rgb(var(--v-theme-surface));
  --af-green: #3f998f;
  --af-green-soft: rgba(63, 153, 143, 0.14);

  max-width: 1160px;
  padding-top: 24px;
  padding-bottom: 48px;
  font-family: 'DM Sans', Arial, sans-serif;
  font-size: 14px;
  line-height: 1.45;
  color: var(--af-ink);
}

.af-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.af-title {
  font: 500 32px/1.2 'Playfair Display', Georgia, serif;
  margin: 0;
}

.af-range {
  display: inline-flex;
  background: var(--af-inset);
  border-radius: 8px;
  padding: 4px;
}

.af-range-btn {
  padding: 6px 12px;
  min-height: 32px;
  border: 0;
  border-radius: 6px;
  background: none;
  font: inherit;
  font-size: 14px;
  color: var(--af-body);
  white-space: nowrap;
  cursor: pointer;
}

.af-range-btn.on {
  background: var(--af-card);
  color: var(--af-ink);
  font-weight: 500;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}

.af-range-btn:focus-visible,
.af-chip:focus-visible,
.af-export:focus-visible,
.af-link:focus-visible,
.af-more:focus-visible {
  outline: 2px solid var(--af-green);
  outline-offset: 2px;
}

.af-period {
  margin: -8px 0 16px;
  color: var(--af-muted);
  font-size: 14px;
}

.af-dates {
  display: flex;
  gap: 12px;
  margin: -4px 0 20px;
}

.af-date {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: var(--af-muted);
}

.af-date input {
  height: 40px;
  border: 1px solid var(--af-line);
  border-radius: 8px;
  padding: 0 12px;
  font: 16px 'DM Sans', Arial, sans-serif;
  color: var(--af-ink);
  background: var(--af-card);
}

.af-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 16px 40px;
  padding: 20px 24px;
  background: var(--af-card);
  border-radius: 12px;
  margin-bottom: 12px;
}

.af-stat b {
  display: block;
  font-size: 24px;
  font-weight: 500;
  line-height: 1.2;
}

.af-stat span {
  font-size: 12px;
  color: var(--af-muted);
}

.af-followups {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 20px;
  margin: 0 0 20px;
  padding: 0 4px;
  font-size: 12px;
  color: var(--af-muted);
}

.af-followups b {
  color: var(--af-ink);
  font-weight: 500;
}

.af-tools {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin: 8px 0 24px;
}

.af-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.af-chip {
  height: 32px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  border-radius: 16px;
  font: 500 12px 'DM Sans', Arial, sans-serif;
  border: 1px solid var(--af-line);
  background: var(--af-card);
  color: var(--af-body);
  white-space: nowrap;
  cursor: pointer;
}

.af-chip .n {
  color: var(--af-muted);
  font-weight: 400;
}

.af-chip.on {
  background: var(--af-green-soft);
  border-color: transparent;
  color: var(--af-ink);
}

.af-chip.on::before {
  content: '✓';
  color: var(--af-green);
}

.af-sep {
  width: 1px;
  height: 20px;
  background: var(--af-line);
  margin: 0 4px;
}

.af-tools-right {
  margin-left: auto;
  display: flex;
  gap: 8px;
  align-items: center;
}

.af-search {
  height: 36px;
  width: 260px;
  border: 1px solid var(--af-line);
  border-radius: 8px;
  padding: 0 12px;
  font: 16px 'DM Sans', Arial, sans-serif;
  color: var(--af-ink);
  background: var(--af-card);
}

.af-export {
  height: 36px;
  padding: 0 14px;
  border: 1px solid var(--af-line);
  border-radius: 8px;
  background: var(--af-card);
  font: 500 14px 'DM Sans', Arial, sans-serif;
  color: var(--af-ink);
  cursor: pointer;
  white-space: nowrap;
}

.af-export:disabled {
  opacity: 0.5;
  cursor: default;
}

.af-state {
  color: var(--af-muted);
  padding: 16px 0;
  margin: 0;
}

.af-error {
  color: rgb(var(--v-theme-error));
}

.af-skeleton-row {
  height: 64px;
  border-radius: 12px;
  background: var(--af-inset);
  margin-bottom: 8px;
}

.af-dim {
  opacity: 0.6;
  transition: opacity 0.2s;
}

.af-group {
  margin-bottom: 32px;
}

.af-group-h {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin: 0 0 12px;
}

.af-group-h h2 {
  font: 500 20px/1.2 'Playfair Display', Georgia, serif;
  margin: 0;
}

.af-group-h span {
  font-size: 14px;
  color: var(--af-muted);
}

.af-person {
  background: var(--af-card);
  border-radius: 12px;
  padding: 16px 24px;
  margin-bottom: 8px;
}

.af-person.compact {
  padding-top: 12px;
  padding-bottom: 12px;
}

.af-person.attention {
  box-shadow: inset 3px 0 0 var(--af-green);
}

.af-person-h {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.af-name {
  font-size: 16px;
  font-weight: 500;
}

.af-meta {
  font-size: 12px;
  color: var(--af-muted);
}

.af-signal {
  color: var(--af-green);
}

.af-last {
  margin-left: auto;
  font-size: 12px;
  color: var(--af-muted);
}

.af-row {
  display: grid;
  grid-template-columns: 72px 1fr;
  gap: 12px;
  padding: 6px 0;
  border-top: 1px solid var(--af-line);
}

.af-person-h + .af-row {
  border-top: 0;
}

.af-t {
  font-size: 12px;
  color: var(--af-muted);
  padding-top: 1px;
  white-space: nowrap;
}

.af-what b {
  font-weight: 500;
}

.af-d {
  color: var(--af-body);
  margin-left: 0.3em;
}

.k-rm .af-what,
.k-rm .af-what b {
  color: var(--af-body);
  font-weight: 400;
}

.af-by {
  display: inline-block;
  font-size: 12px;
  font-weight: 500;
  color: var(--af-muted);
  border: 1px solid var(--af-line);
  border-radius: 4px;
  padding: 0 4px;
  margin-right: 6px;
  line-height: 18px;
}

.af-tag {
  display: inline-block;
  font-size: 12px;
  color: var(--af-green);
  margin-left: 6px;
}

.af-tag-plain {
  color: var(--af-muted);
}

.af-tag-strong {
  font-weight: 600;
}

.af-quotes {
  margin: 4px 0 0;
  padding: 0 0 0 12px;
  border-left: 2px solid var(--af-line);
  color: var(--af-body);
}

.af-quotes > div {
  padding: 2px 0;
}

.af-qt {
  color: var(--af-muted);
  font-size: 12px;
  margin-right: 8px;
}

.af-more,
.af-link {
  border: 0;
  background: none;
  padding: 4px 0;
  font: 500 12px 'DM Sans', Arial, sans-serif;
  color: var(--af-green);
  cursor: pointer;
}

.af-link {
  font-size: 14px;
}

.af-pv .af-what {
  color: var(--af-body);
}

.af-off {
  display: flex;
  gap: 8px 16px;
  flex-wrap: wrap;
  align-items: center;
  padding: 16px 24px;
  background: var(--af-inset);
  border-radius: 12px;
  color: var(--af-body);
}

@media (max-width: 600px) {
  .af-page {
    padding-top: 16px;
  }

  .af-head {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .af-title {
    font-size: 24px;
  }

  .af-range {
    overflow-x: auto;
  }

  .af-summary {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    padding: 16px;
  }

  .af-stat b {
    font-size: 20px;
  }

  .af-tools {
    flex-direction: column;
    align-items: stretch;
  }

  .af-chips {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 4px;
  }

  .af-chip,
  .af-sep {
    flex: none;
  }

  .af-tools-right {
    margin-left: 0;
  }

  .af-search {
    flex: 1;
    width: auto;
    min-width: 0;
  }

  .af-person {
    padding: 12px 16px;
  }

  .af-row {
    grid-template-columns: 60px 1fr;
    gap: 8px;
  }
}
</style>
